import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import * as marked from 'marked';
import { filter, firstValueFrom } from 'rxjs';

import { AppSharedModule } from 'app/app-shared.module';
import { SimpleDialogButtonColor } from 'app/components/simple-dialog.component';
import { AIAgentAPIService } from 'app/providers/ai-agent-api.service';
import { Account, SemantriaAccount } from 'app/providers/bridge-api/account.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { ConfigurationService } from 'app/providers/semantria-api/configuration.service';
import { Group, GroupService } from 'app/providers/semantria-api/group.service';
import { CollectionService } from 'app/providers/storage-api/collection.service';
import { Project, ProjectService } from 'app/providers/storage-api/project.service';
import { ContextBridgeService } from 'app/services/context-bridge.service';
import { DialogService } from 'app/services/dialog.service';
import { LoggerService } from 'app/services/logger.service';
import { NavbarService } from 'app/services/navbar.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import {
  ChatSessionWithName,
  ResearchIdea,
  SpotlightAgentChatService,
} from 'app/services/spotlight-agent-chat.service';
import { AccountUtils } from 'app/utils/account.utils';
import { AIToolService } from '../../services/aitool.service';
import { AlertService } from '../../services/alert.service';
import { AssistantGuardService } from '../../services/assistant-guard.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { SpotlightAssistantChatHistoryPanelComponent } from './chat-history-panel/spotlight-assistant-chat-history-panel.component';
import { SpotlightAssistantMessageListComponent } from './message-list/spotlight-assistant-message-list.component';
import { SpotlightAssistantMobileMenuComponent } from './mobile-menu/spotlight-assistant-mobile-menu.component';
import { SpotlightAssistantAskModeHelper } from './spotlight-assistant-ask-mode.helper';
import { SpotlightAssistantDataSourceHelper } from './spotlight-assistant-data-source.helper';
import { SpotlightAssistantLayoutHelper } from './spotlight-assistant-layout.helper';
import { SpotlightAssistantRequestHelper } from './spotlight-assistant-request.helper';
import {
  AnalysisLoadResult,
  ChartToolOutput,
  DataSource,
  Message,
  StoredChatMessage,
} from './spotlight-assistant.component.model';

export type AssistantMode = 'ask' | 'edit';

const MOBILE_BREAKPOINT = 768;
const DEFAULT_DRAWER_WIDTH = 400;
const MIN_DRAWER_WIDTH = 320;

@EnableClassTelemetry()
@Component({
  selector: 'app-spotlight-assistant',
  templateUrl: './spotlight-assistant.component.html',
  styleUrls: ['./spotlight-assistant.component.scss'],
  imports: [
    AppSharedModule,
    SpotlightAssistantMessageListComponent,
    SpotlightAssistantChatHistoryPanelComponent,
    SpotlightAssistantMobileMenuComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '(window:resize)': 'onResize()',
  },
})
export class SpotlightAssistantComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() protected otelService = injectComponentTelemetryService();
  private readonly translocoService = inject(TranslocoService);
  private readonly agentApiService = inject(AIAgentAPIService);
  private readonly aiToolService = inject(AIToolService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly emptyHtml = this.sanitizer.bypassSecurityTrustHtml('');
  private readonly bridgeService = inject(ContextBridgeService);
  private readonly accountService = inject(SemantriaAccount);
  private readonly authService = inject(AuthService);
  private readonly alertService = inject(AlertService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly spotlightAgentChatService = inject(SpotlightAgentChatService);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly collectionService = inject(CollectionService);
  private readonly dialogService = inject(DialogService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly groupService = inject(GroupService);
  private readonly configService = inject(ConfigurationService);
  private readonly projectService = inject(ProjectService);
  private readonly navbarService = inject(NavbarService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly assistantGuardService = inject(AssistantGuardService);
  private readonly logger = inject(LoggerService);

  private openAssistantHandler = this.handleOpenAssistant.bind(this);
  private readonly askModeHelper = new SpotlightAssistantAskModeHelper(this.logger);
  private readonly dataSourceHelper = new SpotlightAssistantDataSourceHelper(this.logger);
  private readonly layoutHelper = new SpotlightAssistantLayoutHelper();
  private readonly requestHelper = new SpotlightAssistantRequestHelper(this.logger);
  private activeAbortController: AbortController | null = null;
  private activeAskRequestId = 0;

  protected readonly isOpen = signal(false);
  protected readonly isTyping = signal(false);
  protected readonly isWaitingForResponse = signal(false);
  protected readonly mode = signal<AssistantMode>('ask');
  protected readonly isFullscreen = signal(false);
  protected readonly isPinned = signal(false);
  protected readonly pinnedTranslateX = signal<number>(0);
  protected readonly isDraggingPinned = signal(false);
  protected readonly drawerWidth = signal<number>(DEFAULT_DRAWER_WIDTH);
  protected readonly isResizingDrawer = signal(false);
  protected readonly isMobile = signal(false);
  protected readonly messages = signal<Message[]>([]);
  protected readonly visibleMessages = computed(() =>
    this.messages().filter((message) => message.mode === this.mode())
  );
  protected readonly dataSources = signal<DataSource[]>([]);
  protected readonly selectedDataSource = signal<DataSource | null>(null);
  protected readonly previousDataSource = signal<DataSource | null>(null);
  protected readonly showDataSourceSelector = signal(false);
  protected readonly researchIdeas = signal<ResearchIdea[]>([]);
  protected readonly isLoadingIdeas = signal(false);
  protected readonly chatSessions = signal<ChatSessionWithName[]>([]);
  protected readonly showHistoryPanel = signal(false);
  protected readonly filterByDataSource = signal(true);
  protected readonly currentSessionId = signal<string | null>(null);
  protected readonly showMobileMenu = signal(false);
  protected readonly mobileGroups = signal<Group[]>([]);
  protected readonly mobileSelectedGroup = signal<Group | null>(null);
  protected readonly mobileProjects = signal<Project[]>([]);
  protected readonly mobileSelectedProject = signal<Project | null>(null);
  protected readonly mobileProjectsLoading = signal(false);
  // TODO(DASH-XXXX): Re-enable Edit mode when the separate Jira ticket ships.
  protected readonly isEditEnabled = signal(false);
  private readonly isReadOnlyForCurrentGroup = signal(true);
  protected readonly isAgentEnabled = computed(
    () =>
      this.accountLoaded() &&
      !!this.account &&
      AccountUtils.hasFeature(this.account, 'SPOTLIGHT_AI_ASSISTANT') &&
      (this.authService.isAccountAdmin || !this.isReadOnlyForCurrentGroup())
  );

  protected readonly isDrawerVisible = computed(() => this.isOpen() && this.isAgentEnabled());

  protected readonly drawerTransform = computed((): string | null => {
    if (!this.isPinned() || this.isFullscreen() || this.isMobile()) {
      return null;
    }
    return `translateX(${this.pinnedTranslateX()}px)`;
  });
  protected readonly drawerWidthStyle = computed((): string | null => {
    if (this.isFullscreen() || this.isMobile()) {
      return null;
    }
    return `${this.drawerWidth()}px`;
  });
  private readonly currentPath = signal(window.location.pathname);
  protected readonly isEditAvailable = computed(() => {
    if (!this.isEditEnabled()) {
      return false;
    }
    const url = this.currentPath();
    return url.includes('/dashboards/') || /^\/d\//.test(url);
  });
  protected readonly shouldShowFab = computed(() => {
    const url = this.currentPath();
    const isProjectRoute = /\/groups\/[^/]+\/projects(\/|$)/.test(url);
    return this.isAgentEnabled() && isProjectRoute;
  });

  protected readonly fg = new FormGroup({
    input: new FormControl(''),
  });

  private readonly inputValue = signal<string>('');
  protected readonly canSend = computed((): boolean => {
    return !this.isTyping() && this.inputValue().trim().length > 0;
  });

  private readonly accountLoaded = signal(false);
  private account: Account;
  protected projectId: string;
  private dashboardId: string;
  private isInitialized = false;
  private isInitializing = false;
  private lastGroup: Group | null = null;
  private skipGroupPrompt = false;
  private skipProjectPrompt = false;

  private beginAskRequest(): number {
    this.activeAskRequestId += 1;
    this.activeAbortController?.abort();
    this.activeAbortController = null;
    return this.activeAskRequestId;
  }

  private invalidateActiveAskRequest(): void {
    this.activeAskRequestId += 1;
    this.activeAbortController?.abort();
    this.activeAbortController = null;
  }

  private isAskRequestCurrent(requestId: number): boolean {
    return this.activeAskRequestId === requestId;
  }

  private hasActiveConversation(): boolean {
    return this.messages().some(
      (message, index) => message.role === 'user' || (message.role === 'assistant' && index > 0)
    );
  }

  private extractProjectIdFromUrl(pathname: string): string {
    const projectMatch = pathname.match(/\/projects\/([^\/]+)/);
    return projectMatch ? projectMatch[1] : '';
  }

  private async confirmContextSwitch(kind: 'group' | 'project', name: string): Promise<boolean> {
    const dialogRef = this.dialogService.showSimpleDialog({
      title: this.translocoService.translate(`ai-assistant.switch-${kind}-title`),
      content: this.translocoService.translate(`ai-assistant.switch-${kind}-content`, { name }),
      confirmTitle: this.translocoService.translate('ai-assistant.switch-context-confirm'),
      cancelTitle: this.translocoService.translate('ai-assistant.switch-context-cancel'),
      confirmColor: SimpleDialogButtonColor.blue,
    });
    return (await firstValueFrom(dialogRef.afterClosed())) === true;
  }

  private async confirmCollectionSwitch(collectionName: string): Promise<boolean> {
    const dialogRef = this.dialogService.showSimpleDialog({
      title: this.translocoService.translate('ai-assistant.switch-collection-title'),
      content: this.translocoService.translate('ai-assistant.switch-collection-content', {
        name: collectionName,
      }),
      confirmTitle: this.translocoService.translate('ai-assistant.switch-collection-confirm'),
      cancelTitle: this.translocoService.translate('ai-assistant.switch-collection-cancel'),
      confirmColor: SimpleDialogButtonColor.blue,
    });
    return (await firstValueFrom(dialogRef.afterClosed())) === true;
  }

  async ngOnInit(): Promise<void> {
    marked.setOptions({ gfm: true, breaks: true });
    this.inputValue.set(this.fg.controls.input.value ?? '');
    this.fg.controls.input.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.inputValue.set(value ?? '');
      });
    this.checkMobile();
    this.extractRouteParams();
    this.router.events
      .pipe(
        filter((event: unknown) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(async () => {
        this.currentPath.set(this.router.url);
        await this.ensureModeIsValidForRoute();
        await this.initializeAssistant();
      });
    this.groupService.selectedGroup$
      .pipe(
        filter((group: Group | null): group is Group => !!group),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(async (group) => {
        this.isReadOnlyForCurrentGroup.set(this.authService.isReadOnly);
        await this.handleGroupChange(group);
      });
    this.authService.loggedout.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.handleLogout();
    });
    this.assistantGuardService.registerGroupSwitchGuard((groupName: string) => {
      if (!this.hasActiveConversation()) {
        return Promise.resolve(true);
      }
      return this.confirmContextSwitch('group', groupName);
    });
    await this.initializeAssistant();
  }

  private async initializeAssistant(): Promise<void> {
    if (this.isInitialized || this.isInitializing || !this.authService.isLoggedIn()) {
      return;
    }
    this.isInitializing = true;
    this.isReadOnlyForCurrentGroup.set(this.authService.isReadOnly);
    this.account = await this.accountService.sub.latest();
    this.accountLoaded.set(true);
    await this.loadChat();
    await this.ensureGreetingForMode(this.mode());
    await this.loadDataSources();
    this.syncPinnedLayout();
    window.addEventListener('open-ai-assistant', this.openAssistantHandler);
    this.isInitialized = true;
    this.isInitializing = false;
  }

  ngOnDestroy(): void {
    this.invalidateActiveAskRequest();
    this.assistantGuardService.unregisterGroupSwitchGuard();
    window.removeEventListener('open-ai-assistant', this.openAssistantHandler);
    document.documentElement.classList.remove('ai-assistant--pinned');
    this.layoutHelper.detachPinnedDrag((isDragging: boolean) =>
      this.isDraggingPinned.set(isDragging)
    );
    this.layoutHelper.detachDrawerResize((isResizing: boolean) =>
      this.isResizingDrawer.set(isResizing)
    );
  }

  private handleLogout(): void {
    this.invalidateActiveAskRequest();
    this.isOpen.set(false);
    this.isFullscreen.set(false);
    this.isPinned.set(false);
    this.pinnedTranslateX.set(0);
    this.resetChatContext();
    this.accountLoaded.set(false);
    this.account = undefined;
    this.projectId = '';
    this.dashboardId = '';
    this.isInitialized = false;
    this.isInitializing = false;
    window.removeEventListener('open-ai-assistant', this.openAssistantHandler);
    document.documentElement.classList.remove('ai-assistant--pinned');
    this.layoutHelper.detachPinnedDrag((isDragging: boolean) =>
      this.isDraggingPinned.set(isDragging)
    );
  }

  protected async toggleMobileMenu(): Promise<void> {
    const opening = !this.showMobileMenu();
    this.showMobileMenu.set(opening);
    if (opening) {
      this.loadMobileGroups();
      await this.loadMobileProjects();
    }
  }

  private loadMobileGroups(): void {
    const groups = this.groupService.getVisibleGroups() ?? [];
    const selected = this.groupService.getSelectedGroup() ?? null;
    this.mobileGroups.set(groups);
    this.mobileSelectedGroup.set(selected);
  }

  private async loadMobileProjects(): Promise<void> {
    this.mobileProjectsLoading.set(true);
    try {
      const result = await this.projectService.searchProjects(null, 100, true, true);
      const projects = (result.items ?? []).filter(
        (p) => p.status !== 'DELETION_REQUESTED' && p.status !== 'DELETION_IN_PROGRESS'
      );
      this.mobileProjects.set(projects);
      // Try to match the current projectId to highlight the selected project
      const current = projects.find((p) => p.id === this.projectId) ?? null;
      this.mobileSelectedProject.set(current);
    } catch (error) {
      this.logger.error('[SpotlightAssistant] Failed to load mobile projects', { error });
      this.mobileProjects.set([]);
    } finally {
      this.mobileProjectsLoading.set(false);
    }
  }

  protected async selectMobileGroup(group: Group): Promise<void> {
    if (group.group_id === (this.mobileSelectedGroup()?.group_id ?? null)) {
      return;
    }
    this.skipGroupPrompt = true;
    this.groupService.setSelectedGroup(group);
    this.mobileSelectedGroup.set(group);
    this.navbarService.rebuildNavbar();
    await this.loadMobileProjects();
    if (this.mobileProjects().length === 0) {
      this.resetChatContext();
      this.previousDataSource.set(null);
      this.dataSources.set([]);
      this.projectId = '';
      this.dashboardId = '';
      this.showMobileMenu.set(false);
      this.setOpen(false);
    } else {
      this.mobileSelectedProject.set(null);
    }
  }

  protected async selectMobileProject(project: Project): Promise<void> {
    if (project.id === (this.mobileSelectedProject()?.id ?? null)) {
      return;
    }
    if (this.hasActiveConversation()) {
      const confirmed = await this.confirmContextSwitch('project', project.name);
      if (!confirmed) {
        return;
      }
    }
    this.skipProjectPrompt = true;
    this.mobileSelectedProject.set(project);
    this.showMobileMenu.set(false);
    this.resetChatContext();
    this.projectId = project.id;
    await this.loadDataSources();
    await this.loadChat();
    await this.ensureGreetingForMode(this.mode());
    const groupId = this.groupService.getSelectedGroupId(true);
    this.router.navigateByUrl(`/groups/${groupId}/projects/${project.id}`);
  }

  protected async mobileLogout(): Promise<void> {
    this.showMobileMenu.set(false);
    this.setOpen(false);
    this.configService.clearEvaluationConfigs();
    await this.authService.logout();
  }

  private async handleOpenAssistant(event: Event): Promise<void> {
    const customEvent = event as CustomEvent<{
      fullscreen?: boolean;
      mode?: AssistantMode;
      projectId?: string;
      collectionId?: string;
    }>;
    const { fullscreen, mode, projectId, collectionId } = customEvent.detail || {};

    if (fullscreen) {
      this.isFullscreen.set(true);
    }
    let resolvedMode: AssistantMode | undefined;
    if (mode) {
      resolvedMode = mode === 'edit' && !this.isEditAvailable() ? 'ask' : mode;
    }
    if (resolvedMode) {
      this.mode.set(resolvedMode);
    }

    // Reset chat if project changed, or load data sources if empty
    if (projectId && projectId !== this.projectId) {
      this.resetChatContext();
      this.projectId = projectId;
      await this.loadDataSources();
    } else if (projectId && this.dataSources().length === 0) {
      this.projectId = projectId;
      await this.loadDataSources();
    }

    // If projectId and collectionId provided, pre-select the data source
    if (projectId && collectionId && resolvedMode === 'ask') {
      await this.preselectDataSource(projectId, collectionId);
    }

    this.setOpen(true);

    if (resolvedMode) {
      await this.ensureGreetingForMode(resolvedMode);
    }
  }

  private async preselectDataSource(projectId: string, collectionId: string): Promise<void> {
    if (this.dataSources().length === 0) {
      await this.loadDataSourcesForProject(projectId);
    }
    const dataSource = this.dataSources().find(
      (source) => source.projectId === projectId && source.collectionId === collectionId
    );
    if (dataSource) {
      await this.selectDataSource(dataSource);
    }
  }

  protected onResize(): void {
    this.checkMobile();
    this.clampDrawerWidth();
    this.clampPinnedTranslateX();
  }

  private checkMobile(): void {
    this.isMobile.set(window.innerWidth <= MOBILE_BREAKPOINT);
    this.syncPinnedLayout();
  }

  private extractRouteParams(): void {
    const params = this.route.snapshot.paramMap;
    this.projectId = params.get('projectId') ?? '';
    this.dashboardId = params.get('dashboardId') ?? '';

    // Subscribe to route changes to update projectId dynamically
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (routeParams) => {
      const newProjectId = routeParams.get('projectId') ?? '';
      const newDashboardId = routeParams.get('dashboardId') ?? '';

      if (this.skipProjectPrompt) {
        this.skipProjectPrompt = false;
        this.projectId = newProjectId;
        this.dashboardId = newDashboardId;
        return;
      }

      // Reset chat if project or dashboard changes
      if (newProjectId && newProjectId !== this.projectId) {
        const nextProjectId = newProjectId;
        await this.handleProjectChange(nextProjectId, newDashboardId);
      } else if (newDashboardId && newDashboardId !== this.dashboardId) {
        this.dashboardId = newDashboardId;
        this.resetChatContext();
      }
    });
  }

  private async handleProjectChange(nextProjectId: string, nextDashboardId: string): Promise<void> {
    if (this.hasActiveConversation()) {
      const confirmed = await this.confirmContextSwitch('project', nextProjectId);
      if (!confirmed) {
        this.skipProjectPrompt = true;
        const groupId = this.groupService.getSelectedGroupId(true);
        const fallbackUrl = this.projectId
          ? `/groups/${groupId}/projects/${this.projectId}`
          : `/groups/${groupId}/projects`;
        this.router.navigateByUrl(fallbackUrl);
        return;
      }
    }
    this.projectId = nextProjectId;
    this.dashboardId = nextDashboardId;
    this.resetChatContext();
    await this.loadDataSources();
  }

  private async handleGroupChange(group: Group): Promise<void> {
    if (this.skipGroupPrompt) {
      this.skipGroupPrompt = false;
      this.lastGroup = group;
      return;
    }
    if (!this.lastGroup) {
      this.lastGroup = group;
      return;
    }
    if (group.group_id === this.lastGroup.group_id) {
      return;
    }
    this.lastGroup = group;
    await this.resetAssistantForGroupChange();
  }

  private async resetAssistantForGroupChange(): Promise<void> {
    this.setOpen(false);
    this.showMobileMenu.set(false);
    this.resetChatContext();
    this.previousDataSource.set(null);
    this.dataSources.set([]);
    this.projectId = '';
    this.dashboardId = '';
    this.isInitializing = false;
    await this.loadChat();
  }

  private async loadDataSources(): Promise<void> {
    if (!this.projectId) {
      return;
    }
    const snapshotProjectId = this.projectId;
    try {
      const result = await this.collectionService.getCollections(snapshotProjectId);
      if (this.projectId !== snapshotProjectId) {
        return;
      }
      const incompatibleReason = this.translocoService.translate('ai-assistant.incompatible');
      this.dataSources.set(
        this.dataSourceHelper.mapCollectionsToDataSources(
          snapshotProjectId,
          result.items ?? [],
          incompatibleReason
        ) as DataSource[]
      );
    } catch (error) {
      this.logger.error('Failed to load collections for AI assistant:', { error });
    }
  }

  public async loadDataSourcesForProject(projectId: string): Promise<void> {
    this.projectId = projectId;
    await this.loadDataSources();
  }

  protected setOpen(state: boolean): void {
    if (state) {
      this.resolveProjectOnOpen();
    }
    this.isOpen.set(state);
    this.showDataSourceSelector.set(state && this.mode() === 'ask' && !this.selectedDataSource());
    this.clampDrawerWidth();
    this.syncPinnedLayout();
  }

  private resolveProjectOnOpen(): void {
    let currentProjectId = this.route.snapshot.paramMap.get('projectId') ?? '';
    if (!currentProjectId) {
      currentProjectId = this.extractProjectIdFromUrl(window.location.pathname);
    }
    if (currentProjectId && currentProjectId !== this.projectId) {
      this.resetChatContext();
      this.projectId = currentProjectId;
      void this.loadDataSources();
      return;
    }
    if (currentProjectId && this.dataSources().length === 0) {
      this.projectId = currentProjectId;
      void this.loadDataSources();
      return;
    }
    if (!currentProjectId && this.projectId && this.dataSources().length === 0) {
      void this.loadDataSources();
    }
  }

  protected toggleFullscreen(): void {
    this.isFullscreen.update((v) => !v);
    this.clampDrawerWidth();
    this.syncPinnedLayout();
    this.clampPinnedTranslateX();
  }

  protected togglePinned(): void {
    if (this.isMobile()) {
      return;
    }
    const nextPinned = !this.isPinned();
    this.isPinned.set(nextPinned);
    if (!nextPinned) {
      this.pinnedTranslateX.set(0);
      this.layoutHelper.detachPinnedDrag((isDragging: boolean) =>
        this.isDraggingPinned.set(isDragging)
      );
    }
    this.syncPinnedLayout();
    this.clampPinnedTranslateX();
  }

  protected onPinnedDragStart(event: PointerEvent): void {
    this.layoutHelper.startPinnedDrag({
      event,
      isPinned: this.isPinned(),
      isFullscreen: this.isFullscreen(),
      isMobile: this.isMobile(),
      hostElement: this.elementRef.nativeElement,
      currentTranslateX: this.pinnedTranslateX(),
      setTranslateX: (nextValue: number) => this.pinnedTranslateX.set(nextValue),
      setDragging: (isDragging: boolean) => this.isDraggingPinned.set(isDragging),
    });
  }

  protected onDrawerResizeStart(event: PointerEvent): void {
    this.layoutHelper.startDrawerResize({
      event,
      isFullscreen: this.isFullscreen(),
      isMobile: this.isMobile(),
      currentDrawerWidth: this.drawerWidth(),
      minDrawerWidth: MIN_DRAWER_WIDTH,
      maxDrawerWidth: this.getMaxDrawerWidth(),
      setDrawerWidth: (nextValue: number) => this.drawerWidth.set(nextValue),
      setResizing: (isResizing: boolean) => this.isResizingDrawer.set(isResizing),
    });
  }

  private clampPinnedTranslateX(): void {
    this.layoutHelper.clampPinnedTranslateX({
      isPinned: this.isPinned(),
      isFullscreen: this.isFullscreen(),
      isMobile: this.isMobile(),
      hostElement: this.elementRef.nativeElement,
      currentTranslateX: this.pinnedTranslateX(),
      setTranslateX: (nextValue: number) => this.pinnedTranslateX.set(nextValue),
    });
  }

  private clampDrawerWidth(): void {
    if (this.isFullscreen() || this.isMobile()) {
      return;
    }
    const nextWidth = Math.min(
      this.getMaxDrawerWidth(),
      Math.max(MIN_DRAWER_WIDTH, this.drawerWidth())
    );
    if (nextWidth !== this.drawerWidth()) {
      this.drawerWidth.set(nextWidth);
    }
  }

  private getMaxDrawerWidth(): number {
    return Math.max(MIN_DRAWER_WIDTH, window.innerWidth);
  }

  private syncPinnedLayout(): void {
    this.layoutHelper.syncPinnedLayout({
      isPinned: this.isPinned(),
      isDrawerVisible: this.isDrawerVisible(),
      isFullscreen: this.isFullscreen(),
      isMobile: this.isMobile(),
    });
  }

  protected async setMode(newMode: AssistantMode): Promise<void> {
    if (this.mode() === newMode) {
      return;
    }
    if (newMode === 'edit' && !this.isEditAvailable()) {
      return;
    }
    this.mode.set(newMode);
    await this.ensureGreetingForMode(newMode);
    this.showDataSourceSelector.set(newMode === 'ask' && !this.selectedDataSource());
  }

  protected async onPromptKeydown(event: KeyboardEvent): Promise<void> {
    if (event.key !== 'Enter') {
      return;
    }
    if (event.shiftKey) {
      return;
    }
    event.preventDefault();
    await this.send();
  }

  private async ensureModeIsValidForRoute(): Promise<void> {
    if (this.mode() !== 'edit') {
      return;
    }
    if (this.isEditAvailable()) {
      return;
    }
    this.mode.set('ask');
    await this.ensureGreetingForMode('ask');
  }

  private async ensureGreetingForMode(mode: AssistantMode): Promise<void> {
    if (this.messages().some((message) => message.mode === mode)) {
      return;
    }
    await this.addMessage({
      role: 'assistant',
      text: this.getGreetingForMode(mode),
      mode,
    });
  }

  private getGreetingForMode(mode: AssistantMode): string {
    return mode === 'ask'
      ? this.translocoService.translate('ai-assistant.ask-greeting')
      : this.translocoService.translate('ai-assistant.edit-greeting');
  }

  protected async selectDataSource(source: DataSource): Promise<void> {
    if (source.isDisabled) {
      return;
    }
    const currentSource = this.selectedDataSource() ?? this.previousDataSource();
    const isSwitchingCollections =
      !!currentSource && currentSource.collectionId !== source.collectionId;
    const hasExistingAskConversation = this.messages().some(
      (message) => message.mode === 'ask' && message.role === 'user'
    );
    if (isSwitchingCollections && hasExistingAskConversation) {
      const confirmed = await this.confirmCollectionSwitch(source.name);
      if (!confirmed) {
        return;
      }
      await this.newChat();
    }
    this.selectedDataSource.set(source);
    this.previousDataSource.set(null);
    this.showDataSourceSelector.set(false);
    const analysisResult = await this.loadResearchIdeas(source);
    if (!analysisResult.ok) {
      this.markDataSourceIncompatible(source, analysisResult.reason);
      this.selectedDataSource.set(null);
      this.showDataSourceSelector.set(true);
      return;
    }
    await this.loadChatHistory(source.collectionId);
  }

  protected clearDataSource(): void {
    this.previousDataSource.set(this.selectedDataSource());
    this.selectedDataSource.set(null);
    this.showDataSourceSelector.set(true);
    this.researchIdeas.set([]);
    this.chatSessions.set([]);
  }

  protected async cancelDataSourceSelection(): Promise<void> {
    const previous = this.previousDataSource();
    if (!previous) {
      return;
    }
    this.selectedDataSource.set(previous);
    this.previousDataSource.set(null);
    this.showDataSourceSelector.set(false);
    const result = await this.loadResearchIdeas(previous);
    if (!result.ok) {
      this.markDataSourceIncompatible(previous, result.reason);
      this.selectedDataSource.set(null);
      this.showDataSourceSelector.set(true);
      return;
    }
    await this.loadChatHistory(previous.collectionId);
  }

  private async loadResearchIdeas(source: DataSource): Promise<AnalysisLoadResult> {
    this.isLoadingIdeas.set(true);
    try {
      const fallbackReason = this.translocoService.translate('ai-assistant.incompatible');
      const analysisResult = await this.dataSourceHelper.loadResearchIdeas(
        this.spotlightAgentChatService,
        source.projectId,
        source.collectionId,
        fallbackReason,
        source.contentChannel,
        source.hasSections
      );
      this.researchIdeas.set(analysisResult.researchIdeas);
      if (!analysisResult.ok && analysisResult.reason) {
        this.alertService.showToast(analysisResult.reason, 5000, 'error');
      }
      return { ok: analysisResult.ok, reason: analysisResult.reason };
    } finally {
      this.isLoadingIdeas.set(false);
    }
  }

  private markDataSourceIncompatible(source: DataSource, reason?: string): void {
    const fallbackReason = this.translocoService.translate('ai-assistant.incompatible');
    this.dataSources.update(
      (sources) =>
        this.dataSourceHelper.markDataSourceIncompatible(
          sources,
          source.id,
          fallbackReason,
          reason
        ) as DataSource[]
    );
  }

  private async loadChatHistory(collectionId?: string): Promise<void> {
    const effectiveCollectionId = this.filterByDataSource() ? collectionId : undefined;
    const snapshotProjectId = this.projectId;
    const sessions = await this.dataSourceHelper.loadChatSessions(
      this.spotlightAgentChatService,
      snapshotProjectId,
      effectiveCollectionId
    );
    if (this.projectId !== snapshotProjectId) {
      return;
    }
    this.chatSessions.set(sessions);
  }

  protected toggleHistoryPanel(): void {
    this.showHistoryPanel.update((v) => !v);
  }

  protected onHistoryPanelClosed(): void {
    this.showHistoryPanel.set(false);
  }

  protected onHistoryMessagesLoaded(
    loadedMessages: Array<{
      role: 'user' | 'assistant' | 'tool';
      text: string;
      mode: 'ask';
      content: SafeHtml;
      chartData?: ChartToolOutput;
    }>
  ): void {
    this.messages.set(loadedMessages as Message[]);
    this.syncHistory();
  }

  protected onHistoryCurrentSessionChanged(sessionId: string | null): void {
    this.currentSessionId.set(sessionId);
  }

  protected onHistorySessionsUpdated(sessions: ChatSessionWithName[]): void {
    this.chatSessions.set(sessions);
  }

  protected async onHistoryCurrentSessionDeleted(): Promise<void> {
    await this.newChat();
  }

  protected async toggleFilterByDataSource(): Promise<void> {
    this.filterByDataSource.update((v) => !v);
    const collectionId = this.selectedDataSource()?.collectionId;
    await this.loadChatHistory(collectionId);
  }

  protected async onHistoryDataSourceSwitch(collectionId: string): Promise<void> {
    const match = this.dataSources().find((ds) => ds.collectionId === collectionId);
    if (match && match.collectionId !== this.selectedDataSource()?.collectionId) {
      this.selectedDataSource.set(match);
      this.previousDataSource.set(null);
      this.showDataSourceSelector.set(false);
    }
  }

  protected async quickStart(idea: ResearchIdea): Promise<void> {
    this.fg.patchValue({ input: idea.description });
    await this.send();
  }

  protected async sendActionPrompt(prompt: string): Promise<void> {
    this.fg.patchValue({ input: prompt });
    await this.send();
  }

  private resetChatContext(): void {
    this.invalidateActiveAskRequest();
    this.messages.set([]);
    this.currentSessionId.set(null);
    this.selectedDataSource.set(null);
    this.researchIdeas.set([]);
    this.chatSessions.set([]);
    this.showHistoryPanel.set(false);
    this.filterByDataSource.set(true);
    this.spotlightAgentChatService.clearSession();
    this.localStorageService.removeItem('chat-history', 'session');
    this.localStorageService.removeItem('chat-session-id', 'session');
    if (this.mode() === 'ask') {
      this.showDataSourceSelector.set(true);
    }
  }

  protected async newChat(): Promise<void> {
    this.invalidateActiveAskRequest();
    this.messages.set([]);
    this.currentSessionId.set(null);
    this.spotlightAgentChatService.clearSession();
    this.localStorageService.removeItem('chat-history', 'session');
    this.localStorageService.removeItem('chat-session-id', 'session');
    await this.loadChat();
  }

  protected async loadChat(): Promise<void> {
    const historyItem = this.localStorageService.getItem('chat-history', 'session');
    const storedHistory: StoredChatMessage[] = Array.isArray(historyItem)
      ? (historyItem as StoredChatMessage[])
      : [];
    const history: StoredChatMessage[] =
      storedHistory.length > 0
        ? storedHistory
        : [{ role: 'assistant', text: this.getGreetingForMode(this.mode()), mode: this.mode() }];
    const processedMessages: Message[] = [];
    for (const entry of history) {
      processedMessages.push(await this.processMessage({ ...entry }));
    }
    this.messages.set(processedMessages);
  }

  private syncHistory(): void {
    const history: StoredChatMessage[] = this.messages().map(
      ({ content: _content, ...rest }) => rest
    );
    this.localStorageService.setItem('chat-history', history, 'session');
  }

  protected async send(): Promise<void> {
    const { input } = this.fg.getRawValue();
    if (!input?.trim()) {
      return;
    }

    this.isTyping.set(true);
    await this.addMessage({ role: 'user', text: input, mode: this.mode() });
    this.fg.patchValue({ input: '' });

    try {
      if (this.mode() === 'edit') {
        await this.sendEditRequest(input);
      } else {
        await this.sendAskRequest(input);
      }
    } catch (error) {
      this.logger.error('AI Assistant error:', { error });
      await this.addMessage({
        role: 'assistant',
        text: this.translocoService.translate('ai-assistant.error'),
        mode: this.mode(),
      });
    } finally {
      this.isTyping.set(false);
    }
  }

  private async sendEditRequest(input: string): Promise<void> {
    const functions = this.aiToolService.getTools();
    const steps = this.aiToolService.getSteps();
    const context = JSON.stringify(await this.bridgeService.queryData());
    const { text, tools } = await this.agentApiService.chat(input, functions, steps, context);
    this.logger.info('Edit tools called:', { tools });

    for (const { tool, toolInput } of tools) {
      const selection = this.aiToolService.tools.filter((t) => t.name === tool);
      if (selection.length === 1) {
        selection[0].callback(toolInput);
        await this.addMessage({ role: 'tool', text: selection[0].label, mode: 'edit' });
      }
    }
    await this.addMessage({ role: 'assistant', text, mode: 'edit' });
  }

  private async sendAskRequest(input: string): Promise<void> {
    await this.requestHelper.sendAskRequest({
      input,
      mode: 'ask',
      dataSource: this.selectedDataSource(),
      messagesSignal: this.messages,
      spotlightAgentChatService: this.spotlightAgentChatService,
      beginRequest: () => this.beginAskRequest(),
      isRequestCurrent: (requestId: number) => this.isAskRequestCurrent(requestId),
      setActiveAbortController: (controller: AbortController | null) => {
        this.activeAbortController = controller;
      },
      setWaitingForResponse: (isWaiting: boolean) => this.isWaitingForResponse.set(isWaiting),
      setShowDataSourceSelector: (show: boolean) => this.showDataSourceSelector.set(show),
      setCurrentSessionId: (sessionId: string) => this.currentSessionId.set(sessionId),
      updateChatSessions: (updater) => this.chatSessions.update(updater),
      addMessage: async (message) => this.addMessage(message as Message),
      selectDataSourceText: this.translocoService.translate('ai-assistant.select-data-source'),
      genericErrorText: this.translocoService.translate('ai-assistant.error'),
      delegateCallbacks: this.requestHelper.createAskDelegateCallbacks({
        askModeHelper: this.askModeHelper,
        messagesSignal: this.messages,
        sanitizer: this.sanitizer,
        emptyHtml: this.emptyHtml,
        processMessage: async (message) => this.processMessage(message as Message),
        addMessage: async (message) => this.addMessage(message as Message),
        syncHistory: () => this.syncHistory(),
        spotlightAgentChatService: this.spotlightAgentChatService,
        setWaitingForResponse: (value: boolean) => this.isWaitingForResponse.set(value),
        setTyping: (value: boolean) => this.isTyping.set(value),
        errorText: this.translocoService.translate('ai-assistant.error'),
        sessionExpiredText: this.translocoService.translate('ai-assistant.session-expired'),
      }),
    });
  }

  private async processMessage(msg: Message): Promise<Message> {
    return {
      ...msg,
      content: this.sanitizer.bypassSecurityTrustHtml(await marked.parse(msg.text)),
    };
  }

  private async addMessage(msg: Message): Promise<void> {
    const processed = await this.processMessage(msg);
    this.messages.update((msgs) => [...msgs, processed]);
    this.syncHistory();
  }
}
